import { create } from 'zustand'
import type { TextAnalysis, TextDestinationKind } from '../types'
import { EMPTY_MARKS, initialMarks, listMark, listUnmark, type GapMatch, type Marks } from '../utils/textMarks'

/**
 * The text creation flow's state across its three steps (handoff §3–§6).
 *
 * In memory only: a reload starts the flow again at step 1 (the step 2 and 3
 * routes send a learner with no analysis back there). Back from step 2 or 3
 * keeps every mark and choice, as the handoff requires.
 */

export type FlowEntry = 'words' | 'essay'
export type BodySource = 'typed' | 'file' | 'photo'

interface MoveNotice {
  key: string
  label: string
}

interface TextFlowState {
  entry: FlowEntry | null
  /** Where step 1's back goes, and what the desktop bar names. */
  origin: string
  /** What the desktop bar's back link names when the origin is a deck (its title); null otherwise. */
  originLabel: string | null
  body: string
  source: BodySource
  sourceName: string | null

  analysis: TextAnalysis | null
  /** The body the analysis was made from: a changed body needs a new analysis. */
  analysedBody: string | null
  /** Cards, gaps and phrases of step 2, shared by «В тексте» and «Списком». */
  marks: Marks
  /** The server's latest answer for the gap forms (POST /texts/flow/gaps). */
  gapMatch: GapMatch | null
  /** The latest request for occurrences failed: marks stay as tapped. */
  gapMatchFailed: boolean
  notice: MoveNotice | null

  /** null = the default (the first sentence). */
  title: string | null
  /** null = the default for the marks (§6.2). */
  destination: TextDestinationKind | null
  deck: { id: string; title: string } | null
  isActive: boolean
  /** null = the default for the entry (§3). */
  saveText: boolean | null

  /**
   * Begins the flow. With `presetDeck` (the deck screen's «Добавить текст», handoff §8)
   * the destination starts as that existing deck, and leaving the flow goes back to it.
   */
  start: (entry: FlowEntry, origin: string, presetDeck?: { id: string; title: string }) => void
  setBody: (body: string, source: BodySource, sourceName?: string | null) => void
  setAnalysis: (analysis: TextAnalysis, body: string) => void
  setMarks: (marks: Marks) => void
  setGapMatch: (match: GapMatch | null, failed: boolean) => void
  /** The list: a chip moves to «Отмеченные» as Card, with «Отменить». */
  mark: (key: string, label: string) => void
  unmark: (key: string) => void
  undoMove: () => void
  setTitle: (title: string) => void
  setDestination: (kind: TextDestinationKind) => void
  setDeck: (deck: { id: string; title: string }) => void
  setActive: (on: boolean) => void
  setSaveText: (on: boolean) => void
  reset: () => void
}

const EMPTY = {
  entry: null,
  origin: '/library',
  originLabel: null as string | null,
  body: '',
  source: 'typed' as BodySource,
  sourceName: null,
  analysis: null,
  analysedBody: null,
  marks: EMPTY_MARKS,
  gapMatch: null as GapMatch | null,
  gapMatchFailed: false,
  notice: null as MoveNotice | null,
  title: null,
  destination: null,
  deck: null,
  isActive: true,
  saveText: null,
}

export const useTextFlowStore = create<TextFlowState>((set, get) => ({
  ...EMPTY,

  start: (entry, origin, presetDeck) =>
    set(presetDeck
      ? { ...EMPTY, entry, origin, originLabel: presetDeck.title, deck: presetDeck, destination: 'existing_deck' }
      : { ...EMPTY, entry, origin }),

  setBody: (body, source, sourceName = null) =>
    set((s) => ({
      body,
      source,
      sourceName: source === 'typed' ? null : (sourceName ?? s.sourceName),
    })),

  setAnalysis: (analysis, body) =>
    set({
      analysis, analysedBody: body, marks: initialMarks(analysis),
      gapMatch: null, gapMatchFailed: false, notice: null,
    }),

  setMarks: (marks) => set({ marks, notice: null }),
  setGapMatch: (gapMatch, gapMatchFailed) => set({ gapMatch, gapMatchFailed }),

  mark: (key, label) =>
    set((s) => (s.marks.cards.includes(key) ? s : { marks: listMark(s.marks, key), notice: { key, label } })),

  unmark: (key) =>
    set((s) => (s.analysis ? { marks: listUnmark(s.analysis.words, s.marks, key), notice: null } : s)),

  undoMove: () => {
    const { notice, analysis } = get()
    if (notice && analysis) set((s) => ({ marks: listUnmark(analysis.words, s.marks, notice.key), notice: null }))
  },

  setTitle: (title) => set({ title }),
  setDestination: (destination) => set({ destination }),
  setDeck: (deck) => set({ deck, destination: 'existing_deck' }),
  setActive: (isActive) => set({ isActive }),
  setSaveText: (saveText) => set({ saveText }),
  reset: () => set({ ...EMPTY }),
}))
